import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { locale } from './add-chart-dialog.locale';
import {
  ChartFilterPatternNameEnum,
  ChartType,
  ChartTypeEnum,
  CustomControlsNameEnum,
  DateType,
  ICardChartData,
  IChartDialog,
  IChartFilter,
  IChartFilterPattern,
  IOptionInfoStrikes,
  OptionType,
  OptionTypeEnum,
  Pattern,
  Realtime,
  RealTimeEnum,
  SystemControlsNameEnum,
  TimeRange,
  TimeRangeEnum
} from '../../../interfaces';
import {
  Instrument,
  StockTypeEnum
} from '@iv/model/instrument';
import { Destroyer } from 'app/common/utils';
import {
  filter,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { ChartsCommonService } from '../../../services/charts-common.service';
import { ChartsDashboardStateService } from '../../../state/charts-dashboard-state.service';
import {
  dropdownTemplateField,
  isIntraday
} from '../../../helpers/charts-module-helpers';
import { ChartsHttpService } from '../../../services/charts-http.service';
import {
  addSingleContractAnalyzerExpirations,
  addSingleContractAnalyzerStrikes,
  totalOiChartType
} from '../../../state/data-state.mock';

interface ISelectedChart {
  id: ChartType;
  name: string;
}

@Component({
  selector: 'iv-live-add-chart-dialog',
  templateUrl: './add-chart-dialog.component.html',
  styleUrls: ['./add-chart-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
}) // TODO: Видимо нужно этот компоненент тоже разбить на несколько уровней абстракции
export class AddChartDialogComponent extends Destroyer implements OnInit, OnDestroy {

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT) private readonly context: TuiDialogContext<ICardChartData, IChartDialog>,
    private readonly chartsCommonService: ChartsCommonService,
    private readonly chartsDashboardStateService: ChartsDashboardStateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly chartsHttpService: ChartsHttpService
  ) {
    super();

    this.context.data.listChartTypes
      .pipe(map((charts: ChartType[]) => {
        return charts.map((chart: ChartType) => {
          return {
            id: chart,
            name: this.chartsCommonService.getChartName(chart)
          };
        });
      }))
      .subscribe((list: ISelectedChart[]) => this.listChartTypes = list);
  }

  public get listStocks(): Instrument[] {
    return this.context.data.instruments;
  }

  public get isMaturity(): boolean {
    return this.chartsCommonService.isMaturity(this.selectedChart.id);
  }

  public listChartTypes: ISelectedChart[] = [];
  public selectedChart: ISelectedChart;
  public filterTypes = {...ChartFilterPatternNameEnum, ...CustomControlsNameEnum, ...SystemControlsNameEnum};

  public parameters: IChartFilter[] = [];
  public selectedStock: Instrument;
  public locale = locale;
  public filterPattern: { [patternId: string]: IChartFilterPattern<Pattern>[] } = {};

  public chartNameId = ChartTypeEnum;
  public noData = false;
  public errorText = 'There is no data for selected stock'
  public form = new FormGroup({
    chartIdName: new FormControl(null, Validators.required),

    filters: new FormGroup({})
  });

  public get filters(): FormGroup {
    return this.form.get('filters') as FormGroup;
  }

  private allParameters: IChartFilter[] = this.chartsDashboardStateService.allParameters.map(param => {
    return {...param};
  });

  public ngOnInit(): void {
    if (this.context.data.chartVariant) {
      this.selectedChart = {
        id: this.context.data.chartVariant,
        name: this.chartsCommonService.getChartName(this.context.data.chartVariant)
      };
    }

    this.initFormSubscriptions();
    this.chartIdSubscription()
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public dropdownTemplateField(controlName: string): { list: string, input: string } {
    return dropdownTemplateField(controlName);
  }

  public submit(): void {
    this.filters.get(ChartFilterPatternNameEnum.REALTIME)?.enable();
    let selectedFilters = this.filters.value;

    if (this.selectedChart.id === ChartTypeEnum.TERM_STRUCTURE_ROLLING && !this.filters.value?.datetime) {
      selectedFilters = {
        ...selectedFilters,
        datetime: ''
      };
    }

    if (this.selectedChart.id === ChartTypeEnum.SPOT_CHART) {
      selectedFilters = {
        ...selectedFilters,
        [CustomControlsNameEnum.V_MODE_TOGGLER]: selectedFilters.vModeToggler ? selectedFilters.vModeToggler : false
      };
    }

    this.context.completeWith({
      chart: this.selectedChart.id,
      stock: this.selectedStock,
      selectedFilters,
      filters: this.parameters
    });
  }

  public autocompleteItemClick(event: Instrument) {
    this.selectedStock = event;
  }

  // TODO: Не понятно зачем здесь previousValue, оно нигде не используется (refac)
  public updateFormValidity(event: { value: string, previousValue: string }, control: FormControl): void {
    if (!event.value) {
      control.setValue(null);
      this.noData = false;
      this.changeDetectorRef.detectChanges()
    }

    if (event.value !== this.selectedStock?.nameAndExchange) {
      this.form.setErrors({differencesWithSelectedStock: true});
    }
  }

  public getFilterName(filterKey: string): string {
    return this.parameters.find((chartFilter: IChartFilter) => chartFilter.parameterId === filterKey).name;
  }

  public dateIsEod(flag: boolean): void {
    const isRealtimeControl = this.filters.get(ChartFilterPatternNameEnum.REALTIME);

    if (flag) {
      isRealtimeControl.disable();
      isRealtimeControl.setValue(
        this.filterPattern[ChartFilterPatternNameEnum.REALTIME].find((value: IChartFilterPattern<Realtime>) => value.name === RealTimeEnum.OFF)
      );
    } else {
      isRealtimeControl.enable();
    }
  }

  public getChartDescription(selectedChart: ISelectedChart): string {
    switch (selectedChart.id) {
      case ChartTypeEnum.SPOT_CHART:
        return 'History of the asset’s Implied Price';
      case ChartTypeEnum.IVX_HISTORY:
        return 'History of the asset’s Volatility Index';
      case ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME:
        return 'Historical Open Interest and Volumes';
      case ChartTypeEnum.HISTORICAL_VOLATILITY:
        return 'History of the asset’s Realized Volatility';
      case ChartTypeEnum.IMPLIED_VOLATILITY:
        return 'History of Implied Volatility for a specified Term and Moneyness';
      case ChartTypeEnum.TERM_STRUCTURE_ROLLING:
        return 'Level of the Implied Volatility for various terms';
      case ChartTypeEnum.SKEW_CHART:
        return 'Level of Implied Volatility for various moneyness';
      case ChartTypeEnum.PREMIUM_CHART:
        return 'Intraday Data available for IVX only. Historical Volatility in EOD mode';
      case ChartTypeEnum.IV_DIFFERENCE:
        return 'Plot the difference between any two points on the implied volatility surface';
      case ChartTypeEnum.SINGLE_CONTRACT_ANALYZER:
        return 'Analyze the price and volatility of a specific option contract';
    }
  }

  private initFormSubscriptions(): void {
    let rtSub: Subscription;
    let stockControlSub: Subscription;
    const {chartIdName} = this.form.controls;

    chartIdName.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((chartVariant: ISelectedChart) => {
        this.selectedChart = {id: null, name: null};
        if (rtSub) rtSub.unsubscribe();
        if (stockControlSub) stockControlSub.unsubscribe();
        if (chartVariant) {
          this.selectedChart = chartVariant;

          this.removeFiltersForm();
          this.setConfigurationForm(
            this.selectedChart.id === ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME ? totalOiChartType : []
          );

          if (
            this.chartsCommonService.isHistorical(chartVariant.id) &&
            this.selectedChart.id !== ChartTypeEnum.HISTORICAL_VOLATILITY &&
            this.selectedChart.id !== ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME
          ) {
            rtSub = this.historicalChartsRtSub();
          }

          this.selectedChart.id === ChartTypeEnum.IV_DIFFERENCE
            ? stockControlSub?.unsubscribe()
            : stockControlSub = this.stockSubscription();

          if (this.chartsCommonService.isMaturity(chartVariant.id)) {
            rtSub = this.realtimeControlSubscription();
          }
        } else {
          this.removeFiltersForm();
        }
      });

    chartIdName.setValue(this.selectedChart ? this.selectedChart : null);
  }

  private stockSubscription(): Subscription {
    const { vModeToggler, timeRange } = this.filters.controls;

    return this.form.get(ChartFilterPatternNameEnum.STOCK_ID)
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((stockItem: Instrument) => {
        if (!stockItem) {
          if (this.selectedChart.id === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
            this.removeFiltersForm();
          }
          this.selectedStock = null;
          if (this.selectedChart.id === ChartTypeEnum.SPOT_CHART) {
            vModeToggler.disable();
          }
        }

        if (this.selectedChart.id === ChartTypeEnum.SPOT_CHART && stockItem) {
          if (stockItem.stockType === StockTypeEnum.INDEX || stockItem.stockType === StockTypeEnum.FX_INDEX) {
            vModeToggler.setValue(false);
            vModeToggler.disable();
          } else {
            if (
              timeRange.value?.name === TimeRangeEnum.D1 ||
              timeRange.value?.name === TimeRangeEnum.D3 ||
              timeRange.value?.name === TimeRangeEnum.H24
            ) {
              vModeToggler.enable();
            }
          }
        }

        if (this.selectedChart.id === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER && stockItem) {
          this.intraDayOptionControls(stockItem);
        }
      });
  }

  private intraDayOptionControls(stock: Instrument): void {
    this.chartsHttpService.getOptInfoExpirations(stock.stockId)
      .subscribe((expirations: string[]) => {
        if (!expirations.length) {
          this.errorText = 'There is no data for selected stock'
          this.noData = true;
          this.setErrorToSymbolControl()
          return
        }
        this.noData = false;
        this.setConfigurationForm(addSingleContractAnalyzerExpirations(expirations));
        this.filters.get(CustomControlsNameEnum.EXPIRATION_DATE)
          .valueChanges
          .pipe(
            switchMap((value: IChartFilterPattern<DateType>) => {
              return this.chartsHttpService.getOptInfoStrikes(
                {
                  stockId: this.form.get(ChartFilterPatternNameEnum.STOCK_ID).value.stockId,
                  expirationDate: value.name
                }
              );
            }),
            takeUntil(this.destroy$)
          )
          .subscribe((strikes: IOptionInfoStrikes) => {
            this.setConfigurationForm([
              this.parameters.find((param: IChartFilter) => param.parameterId === CustomControlsNameEnum.EXPIRATION_DATE),
              ...addSingleContractAnalyzerStrikes(strikes)
            ]);
            this.filters.get(CustomControlsNameEnum.STRIKE).setValue({description: strikes.center.toString(), name: strikes.center});
            this.changeDetectorRef.detectChanges();
          });
      });
  }

  private realtimeControlSubscription(): Subscription {
    const isRealtimeControl = this.filters.get(ChartFilterPatternNameEnum.REALTIME);

    return isRealtimeControl
      .valueChanges
      .pipe(
        filter(value => value !== null),
        takeUntil(this.destroy$)
      )
      .subscribe((value: IChartFilterPattern<Realtime>) => {
        switch (value.name) {
          case RealTimeEnum.OFF:
            this.filters.addControl(ChartFilterPatternNameEnum.DATE_TIME, new FormControl(null, Validators.required));
            break;
          case RealTimeEnum.ON:
            if (this.filters.get(ChartFilterPatternNameEnum.DATE_TIME)) {
              this.filters.removeControl(ChartFilterPatternNameEnum.DATE_TIME);
              isRealtimeControl.enable();
            }
            break;
        }
      });
  }

  private historicalChartsRtSub(): Subscription {
    const {timeRange, realtime, vModeToggler} = this.filters.controls;

    return timeRange.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((range: IChartFilterPattern<TimeRange>) => {
        if (!range) return;

        if (isIntraday(range.name)) {
          realtime.setValue({name: RealTimeEnum.ON, description: 'On'});
        } else {
          realtime.setValue({name: RealTimeEnum.OFF, description: 'Off'});
        }

        if (isIntraday(range.name)) {
          if (
            this.selectedChart.id === ChartTypeEnum.SPOT_CHART && (
              this.selectedStock?.stockType === StockTypeEnum.STOCK ||
              this.selectedStock?.stockType === StockTypeEnum.FUND
            )
          ) {
            vModeToggler.enable();
          }
        } else {
          if (this.selectedChart.id === ChartTypeEnum.SPOT_CHART) {
            vModeToggler.setValue(false);
            vModeToggler.disable();
          }
        }
      });
  }

  private removeFiltersForm(): void {
    this.form.removeControl('filters');
    this.form.addControl('filters', new FormGroup({}));
    this.changeDetectorRef.detectChanges();
  }

  // TODO: Эта функция занимается сразху двумя вещами - установкой конфига формы при ините и при динамическом изменении количества контролов.
  //  Поведение изменения нужно вынести в отдельную функецию
  private setConfigurationForm(customParams: IChartFilter[] = []): void {
    if (!this.selectedChart) return;

    this.parameters = this.chartsCommonService.getParametersChart(this.selectedChart.id, [...this.allParameters, ...customParams]);

    this.parameters.forEach((parameter: IChartFilter) => {
      if (
        parameter.parameterId === ChartFilterPatternNameEnum.STOCK_ID ||
        parameter.parameterId === ChartFilterPatternNameEnum.DATE_TIME
      ) return;

      this.filterPattern[parameter.parameterId] = parameter.pattern;
      this.filters.addControl(parameter.parameterId, new FormControl(null, Validators.required));

      if (
        this.chartsCommonService.isHistorical(this.selectedChart.id) &&
        parameter.parameterId === ChartFilterPatternNameEnum.REALTIME
      ) {
        this.filters.get(ChartFilterPatternNameEnum.REALTIME).clearValidators();
      }

      if (this.selectedChart.id === ChartTypeEnum.SPOT_CHART) {
        this.filters.addControl(CustomControlsNameEnum.V_MODE_TOGGLER, new FormControl({value: false, disabled: true}));
      }

      if (this.selectedChart.id === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
        // TODO: Когда TIME_RANGE будет больше D1 для SINGLE_CONTRACT_ANALYZER -> убрать
        if (parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE) {
          this.filters.get(ChartFilterPatternNameEnum.TIME_RANGE).setValue({name: TimeRangeEnum.D3, description: TimeRangeEnum.D3});
        }
      }
    });

    this.selectedChart?.id !== ChartTypeEnum.IV_DIFFERENCE
      ? this.form.addControl(ChartFilterPatternNameEnum.STOCK_ID, new FormControl(null, Validators.required))
      : this.form.removeControl(ChartFilterPatternNameEnum.STOCK_ID);

    this.changeDetectorRef.detectChanges();
  }

  private setErrorToSymbolControl(): void {
    this.form.controls['stockId'].setErrors({'incorrect': true})
    this.changeDetectorRef.detectChanges()
  }

  public onStrikeChanged(message: string): void {
    if (message === 'valid') {
      this.noData = false
      this.filters.controls['strike'].setErrors(null)
    } else {
      this.filters.controls['strike'].setErrors({'incorrect': true})
      this.errorText = message
      this.noData = true
    }
  }

  private chartIdSubscription(): void {
    this.form.get('chartIdName').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(chartType => {
      if (chartType.id === 'SINGLE_CONTRACT_ANALYZER') {
        this.intraDayOptionControls(this.selectedStock);
        this.changeDetectorRef.detectChanges()
      }
    })
  }
}
